import { useState, useEffect } from 'react';
import { format, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { notifyAdminAboutBooking } from '../utils/bookingNotifications';

interface Employee {
  Id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  spec_name: string;
  department: any;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

export function PersonalTrainingPage() {
  const navigate = useNavigate();
  const { user, apiClient } = useAuthStore();
  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedTrainer, setSelectedTrainer] = useState<Employee | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('Все');

  // Generate available dates (next 14 days)
  const availableDates = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMMM, EEEE', { locale: ru }),
      isToday: i === 0,
    };
  });

  useEffect(() => {
    loadTrainers();
  }, [apiClient]);

  useEffect(() => {
    if (selectedTrainer && selectedDate) {
      loadFreeTime();
    }
  }, [selectedTrainer, selectedDate]);


  const loadTrainers = async () => {
    if (!apiClient) return;

    setIsLoading(true);
    try {
      const allEmployees = await apiClient.getEmployees();
      // Filter only trainers (not administrators and not tennis trainers)
      const trainers = allEmployees.filter(emp =>
        emp.spec_name?.includes('Тренер') &&
        !emp.spec_name?.includes('Администратор') &&
        !emp.spec_name?.includes('Теннис')
      );
      setEmployees(trainers);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const loadFreeTime = async () => {
    if (!selectedTrainer || !selectedDate || !apiClient) return;

    setIsLoading(true);
    try {
      const freeTimeSlots = await apiClient.getEmpFreeTime(selectedTrainer.Id, selectedDate);


      // Parse real Universe API response structure
      const empFreeTimeData = Array.isArray(freeTimeSlots) ? freeTimeSlots : (freeTimeSlots?.emp_free_time || []);
      const slots: TimeSlot[] = [];

      if (Array.isArray(empFreeTimeData) && empFreeTimeData.length > 0) {
        empFreeTimeData.forEach((empData: any) => {
          if (empData.free_time && Array.isArray(empData.free_time) && empData.free_time.length > 0) {
            empData.free_time.forEach((timeRange: any) => {
              if (timeRange.time_from && timeRange.time_to) {
                // Generate time slots from range
                const startTime = timeRange.time_from; // "06:00"
                const endTime = timeRange.time_to;     // "23:00"
                const interval = empData.interval || 60; // minutes

                // Generate slots every hour for now (можно сделать по interval)
                const startHour = parseInt(startTime.split(':')[0]);
                const endHour = parseInt(endTime.split(':')[0]);

                // Get current time for filtering past slots on today
                const now = new Date();
                const isToday = selectedDate === format(now, 'yyyy-MM-dd');
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();

                for (let hour = startHour; hour < endHour; hour++) {
                  // Skip very early morning and late evening for personal training
                  if (hour >= 9 && hour <= 21) {
                    // For today, skip past time slots
                    const shouldSkipHour = isToday && (hour < currentHour || (hour === currentHour && currentMinute >= 0));
                    const shouldSkipHalfHour = isToday && (hour < currentHour || (hour === currentHour && currentMinute >= 30));

                    if (!shouldSkipHour) {
                      slots.push({
                        time: `${hour.toString().padStart(2, '0')}:00`,
                        available: true
                      });
                    }
                    // Add half-hour slots too
                    if (hour < 21 && !shouldSkipHalfHour) {
                      slots.push({
                        time: `${hour.toString().padStart(2, '0')}:30`,
                        available: true
                      });
                    }
                  }
                }
              }
            });
          }
        });
      }

      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading free time:', error);
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getTrainerSpecialty = (trainer: Employee): string => {
    if (trainer.spec_name?.includes('Теннис')) return 'Теннис';
    if (trainer.spec_name?.includes('Тренажерного зала')) return 'Тренажерный зал';
    if (trainer.spec_name?.includes('падел')) return 'Падел';
    if (trainer.spec_name?.includes('Визажист') || trainer.spec_name?.includes('бровист')) return 'Beauty';
    if (trainer.spec_name?.includes('групповых занятий')) return 'Групповые занятия';
    return trainer.spec_name || 'Тренер';
  };

  const categories = [
    { name: 'Все', icon: '💪' },
    { name: 'Тренажерный зал', icon: '🏋️‍♂️' },
    { name: 'Групповые занятия', icon: '👥' },
  ];

  const filteredEmployees = selectedCategory === 'Все'
    ? employees
    : employees.filter(emp => getTrainerSpecialty(emp) === selectedCategory);

  const getSpecialtyIcon = (specialty: string): string => {
    switch (specialty) {
      case 'Теннис': return '🎾';
      case 'Тренажерный зал': return '🏋️‍♂️';
      case 'Падел': return '🏓';
      case 'Beauty': return '💄';
      case 'Групповые занятия': return '👥';
      default: return '💪';
    }
  };

  const getSpecialtyColor = (specialty: string): string => {
    switch (specialty) {
      case 'Теннис': return 'bg-green-100 text-green-800';
      case 'Тренажерный зал': return 'bg-orange-100 text-orange-800';
      case 'Падел': return 'bg-blue-100 text-blue-800';
      case 'Beauty': return 'bg-pink-100 text-pink-800';
      case 'Групповые занятия': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleBooking = async () => {
    if (!selectedTrainer || !selectedDate || !selectedTime || !user || !apiClient) return;

    setIsLoading(true);
    try {
      // Calculate end time (1 hour after start)
      const [hours, minutes] = selectedTime.split(':');
      const endHour = parseInt(hours) + 1;
      const endTime = `${endHour.toString().padStart(2, '0')}:${minutes}`;

      // Create booking data
      const bookingData = {
        ClientId: user.id,
        EmpId: selectedTrainer.Id,
        date_time_start: `${selectedDate} ${selectedTime}`,
        date_time_end: `${selectedDate} ${endTime}`,
        note: `Персональная тренировка - ${getTrainerSpecialty(selectedTrainer)}`
      };

      console.log('Creating personal training booking:', bookingData);

      const result = await apiClient.createBooking(bookingData);
      console.log('Booking created successfully:', result);

      // Отправляем уведомление админу в Telegram
      await notifyAdminAboutBooking({
        clientId: user.id,
        clientName: `${user.firstName} ${user.lastName}`,
        date: format(new Date(selectedDate), 'dd.MM.yyyy', { locale: ru }),
        time: selectedTime,
        serviceName: `Персональная тренировка - ${getTrainerSpecialty(selectedTrainer)}`,
        employeeName: `${selectedTrainer.first_name} ${selectedTrainer.last_name}`,
        bookingId: result?.id || result?.booking_id || 'N/A'
      });

      // Redirect to profile to see the new booking
      navigate('/profile');
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Ошибка при бронировании. Попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => {
              if (step > 1) setStep(step - 1);
              else navigate('/');
            }}
            className="flex items-center mb-4 hover:opacity-75"
            style={{ color: 'var(--text-secondary)' }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>
          
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Персональные тренировки</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Выберите тренера и удобное время для индивидуального занятия</p>
        </div>

        {/* Progress */}
        <div className="flex items-center mb-8">
          {[1, 2, 3, 4].map((stepNum) => (
            <div key={stepNum} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= stepNum ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {stepNum}
              </div>
              {stepNum < 4 && (
                <div className={`w-12 h-1 ${step > stepNum ? 'bg-emerald-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Trainer Selection */}
        {step === 1 && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>1. Выберите тренера</h2>
              {isLoading && (
                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            {/* Category Filter */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex space-x-2 pb-2">
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => setSelectedCategory(category.name)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors flex items-center space-x-2 ${
                      selectedCategory === category.name
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEmployees.map((trainer) => {
                const specialty = getTrainerSpecialty(trainer);
                return (
                  <button
                    key={trainer.Id}
                    onClick={() => {
                      setSelectedTrainer(trainer);
                      setStep(2);
                    }}
                    className={`p-6 border rounded-2xl text-left hover:border-emerald-500 transition-colors ${
                      selectedTrainer?.Id === trainer.Id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {trainer.first_name.charAt(0)}{trainer.last_name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {trainer.first_name} {trainer.last_name}
                        </h3>
                        {trainer.middle_name && trainer.middle_name !== '!' && (
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{trainer.middle_name}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 text-xs rounded-full ${getSpecialtyColor(specialty)}`}>
                        {getSpecialtyIcon(specialty)} {specialty}
                      </span>
                      <span className="text-2xl">{getSpecialtyIcon(specialty)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {filteredEmployees.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">👨‍🏫</div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  {selectedCategory === 'Все' ? 'Тренеры не найдены' : `Нет тренеров в категории "${selectedCategory}"`}
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  {selectedCategory === 'Все' ? 'Попробуйте обновить страницу' : 'Выберите другую категорию'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Date Selection */}
        {step === 2 && selectedTrainer && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              2. Выберите дату для занятия с {selectedTrainer.first_name} {selectedTrainer.last_name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableDates.map((date) => (
                <button
                  key={date.value}
                  onClick={() => {
                    setSelectedDate(date.value);
                    setStep(3);
                  }}
                  className={`p-4 border rounded-lg text-center hover:border-emerald-500 transition-colors ${
                    selectedDate === date.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                  }`}
                  style={{
                    color: 'var(--text-primary)',
                    backgroundColor: selectedDate === date.value ? 'var(--emerald-bg)' : 'var(--card-bg)',
                    borderColor: selectedDate === date.value ? 'var(--emerald-border)' : 'var(--border-color)'
                  }}
                >
                  <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{date.label}</div>
                  {date.isToday && <div className="text-sm" style={{ color: 'var(--emerald-text)' }}>Сегодня</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Time Selection */}
        {step === 3 && selectedTrainer && selectedDate && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                3. Выберите время на {format(new Date(selectedDate), 'dd MMMM', { locale: ru })}
              </h2>
              {isLoading && (
                <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            

            {availableSlots.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {availableSlots.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => {
                      setSelectedTime(slot.time);
                      setStep(4);
                    }}
                    disabled={!slot.available}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      slot.available
                        ? selectedTime === slot.time
                          ? 'border-emerald-500'
                          : 'border-gray-200 hover:border-emerald-500'
                        : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    style={{
                      color: slot.available ? 'var(--text-primary)' : 'var(--text-tertiary)',
                      backgroundColor: slot.available
                        ? (selectedTime === slot.time ? 'var(--emerald-bg)' : 'var(--card-bg)')
                        : 'var(--bg-secondary)',
                      borderColor: slot.available
                        ? (selectedTime === slot.time ? 'var(--emerald-border)' : 'var(--border-color)')
                        : 'var(--border-color)'
                    }}
                  >
                    {slot.time}
                    {!slot.available && <div className="text-xs text-gray-400">Занято</div>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">На выбранную дату нет свободного времени</p>
                <button
                  onClick={() => setStep(2)}
                  className="text-emerald-600 hover:text-emerald-700"
                >
                  Выбрать другую дату
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && selectedTrainer && selectedDate && selectedTime && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>4. Подтверждение записи</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Тип тренировки:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Персональная тренировка</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Тренер:</span>
                <div className="text-right">
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedTrainer.first_name} {selectedTrainer.last_name}</span>
                  <div className="text-sm text-gray-600">{getTrainerSpecialty(selectedTrainer)}</div>
                </div>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Дата:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{format(new Date(selectedDate), 'dd MMMM yyyy', { locale: ru })}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Время:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedTime} - {format(new Date(`1970-01-01T${selectedTime}:00`), 'HH:mm', { locale: ru }).replace(/(\d{2}):(\d{2})/, (_, h, m) => `${parseInt(h) + 1}:${m}`)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Длительность:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>1 час</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Стоимость:</span>
                <span className="font-bold text-emerald-600 text-lg">2500₽</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setStep(3)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Назад
              </button>
              <button
                onClick={handleBooking}
                className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                Подтвердить запись
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}